/**
 * Kalkulator tkanki tłuszczowej DNŻ: czysta logika obliczeń.
 *
 * Zero DOM, zero zależności. Ten sam plik działa w przeglądarce
 * (window.DnnzBodyFatLogic) i w Node (module.exports), więc testy
 * sprawdzają dokładnie ten kod, który liczy na stronie.
 *
 * Wszystkie liczby przychodzą z konfiguracji inc/data/body-fat.php (w szablonie
 * atrybut data-config, w testach parser tablicy PHP). W tym pliku nie ma
 * żadnej wartości merytorycznej. Źródła: ZRODLA.md, decyzje: DECYZJE.md.
 *
 * Metoda: RFM (Woolcott i Bergman 2018), otyłość według progów autorów
 * (Woolcott i Bergman 2020). Zakres prawidłowy: Gallagher 2000, tabela 4.
 * FFMI: Kouri 1995, pasma populacyjne: Schutz 2002. Jedyne sprawdzenie wejścia:
 * czy wynik jest fizycznie możliwy (DECYZJE D28).
 */
(function (root, factory) {
	'use strict';
	var api = factory();
	if (typeof module === 'object' && module.exports) {
		module.exports = api;
	}
	if (root) {
		root.DnnzBodyFatLogic = api;
	}
}(typeof window !== 'undefined' ? window : null, function () {
	'use strict';

	// --- Liczby ---------------------------------------------------------

	function toNumber(value) {
		if (value === null || value === '' || typeof value === 'undefined') {
			return NaN;
		}
		var parsed = Number(String(value).trim().replace(',', '.'));
		return isFinite(parsed) ? parsed : NaN;
	}

	/**
	 * Ucina śmieci zmiennoprzecinkowe przed zaokrągleniem (jak round6()
	 * w nawodnieniu): 80 × 22 / 100 to w IEEE 754 17,599999999999998.
	 */
	function round6(value) {
		return Math.round(value * 1e6) / 1e6;
	}

	function round1(value) {
		return Math.round(round6(value) * 10) / 10;
	}

	/**
	 * Zapis z przecinkiem dziesiętnym i jednym miejscem po nim: 17.6 → "17,6".
	 * Wartość wejściowa jest już zaokrąglona do 0,1.
	 */
	function formatDecimal(value) {
		var tenths = Math.round(value * 10);
		var sign = tenths < 0 ? '-' : '';
		tenths = Math.abs(tenths);
		return sign + Math.floor(tenths / 10) + ',' + (tenths % 10);
	}

	/**
	 * Odczyt pola liczbowego: { value, empty, invalid }. Puste pole to osobny
	 * stan, nie błąd. Błąd pola = tylko tekst, który nie jest liczbą dodatnią;
	 * pola nie mają zakresów, liczy się tylko to, czy wynik jest możliwy (D28).
	 */
	function readField(raw) {
		var text = raw === null || typeof raw === 'undefined' ? '' : String(raw).trim();
		if (text === '') {
			return { value: null, empty: true, invalid: false };
		}
		var value = toNumber(text);
		if (isNaN(value) || value <= 0) {
			return { value: null, empty: false, invalid: true };
		}
		return { value: value, empty: false, invalid: false };
	}

	// --- Wzory ----------------------------------------------------------

	/**
	 * RFM = base − coef × (wzrost / talia), wzrost i talia w tych samych jednostkach.
	 */
	function rfmBf(sex, m, cfg) {
		if (!(m.waist > 0)) {
			return null;
		}
		return cfg.rfm[sex] - cfg.rfm.coef * (m.height / m.waist);
	}

	/**
	 * Wyświetlany %BF: pełne procenty (DECYZJE D9).
	 */
	function displayBf(raw) {
		return Math.round(round6(raw));
	}

	function inRange(value, limits) {
		return value >= limits.min && value <= limits.max;
	}

	function findRange(sex, age, cfg) {
		if (age === null) {
			return null;
		}
		for (var i = 0; i < cfg.ranges.length; i++) {
			var r = cfg.ranges[i];
			if (r.sex === sex && age >= r.age_min && age <= r.age_max) {
				return r;
			}
		}
		return null;
	}

	/**
	 * Kategoria z wyświetlanego %BF (DECYZJE D9, D21):
	 * obese od progu autorów RFM (bez podziału na wiek), a poniżej niego
	 * low | normal | high według zakresu dla płci i wieku (null bez zakresu).
	 */
	function category(bf, sex, range, cfg) {
		if (bf >= cfg.obesity[sex]) {
			return 'obese';
		}
		if (!range) {
			return null;
		}
		if (bf < range.low) {
			return 'low';
		}
		if (bf <= range.high) {
			return 'normal';
		}
		return 'high';
	}

	function findBand(sex, age, cfg) {
		if (age === null) {
			return null;
		}
		var bands = cfg.ffmi.bands;
		for (var i = 0; i < bands.length; i++) {
			if (bands[i].sex === sex && age >= bands[i].age_min && age <= bands[i].age_max) {
				return bands[i];
			}
		}
		return null;
	}

	/**
	 * Masy i FFMI z wartości wyświetlanych (DECYZJE D9).
	 */
	function composition(weight, heightCm, bf, sex, cfg) {
		var fat = round1(weight * bf / 100);
		var lean = round1(weight - fat);
		var hM = heightCm / 100;
		var ffmi = round1(lean / (hM * hM));
		var normalized = sex === 'm'
			? round1(ffmi + cfg.ffmi.norm_coef * (cfg.ffmi.norm_height_m - hM))
			: null;
		return { fat: fat, lean: lean, ffmi: ffmi, ffmiNormalized: normalized };
	}

	/**
	 * Nadmiar tłuszczu ponad górną granicę zakresu przy stałej masie
	 * beztłuszczowej: X = tłuszcz − beztłuszczowa × high / (100 − high).
	 */
	function excessFat(fat, lean, high) {
		return round1(fat - lean * high / (100 - high));
	}

	// --- Ocena całego wejścia -----------------------------------------------

	/**
	 * input: { sex: 'k'|'m'|'', age, height, weight, waist (teksty pól) }
	 *
	 * impossible: true, gdy z wpisanych wartości wychodzi wynik, którego nie da się
	 * mieć (D28). Wtedy nie ma liczby, jest jedna linia zamiast niej.
	 */
	function evaluate(input, cfg) {
		var sex = input.sex === 'k' || input.sex === 'm' ? input.sex : '';

		var fields = {
			age: readField(input.age),
			height: readField(input.height),
			weight: readField(input.weight),
			waist: readField(input.waist)
		};

		var out = {
			sex: sex,
			fields: fields,
			blocked: null,
			impossible: false,
			hasResult: false,
			bfRaw: null,
			bf: null,
			range: null,
			rangeNote: null,
			category: null,
			composition: null,
			band: null,
			excess: null,
			lowFfmi: false,
			proteinCta: false
		};

		// Blokady mają pierwszeństwo (DECYZJE D15).
		var age = fields.age.value;
		if (age !== null && age < cfg.limits.minor) {
			out.blocked = 'minor';
			return out;
		}

		// Tekst zamiast liczby w którymkolwiek polu: komunikat przy polu, bez wyniku.
		if (fields.age.invalid || fields.height.invalid || fields.weight.invalid || fields.waist.invalid) {
			return out;
		}

		if (sex === '') {
			return out;
		}
		if (fields.height.value === null || fields.waist.value === null) {
			return out;
		}

		var m = { height: fields.height.value, waist: fields.waist.value };
		var raw = rfmBf(sex, m, cfg);
		if (raw === null || !isFinite(raw)) {
			return out;
		}
		var bf = displayBf(raw);
		// Wynik fizycznie niemożliwy: jedna linia zamiast liczby (D28).
		if (!inRange(bf, cfg.possible.bf)) {
			out.impossible = true;
			out.bfRaw = raw;
			return out;
		}
		var sklad = fields.weight.value === null ? null : composition(fields.weight.value, m.height, bf, sex, cfg);
		if (sklad && (sklad.fat > fields.weight.value || !inRange(sklad.ffmi, cfg.possible.ffmi))) {
			out.impossible = true;
			out.bfRaw = raw;
			return out;
		}

		out.hasResult = true;
		out.bfRaw = raw;
		out.bf = bf;

		var years = age === null ? null : Math.floor(age);
		out.range = findRange(sex, years, cfg);
		if (!out.range) {
			out.rangeNote = years === null ? 'no-age' : 'out-of-range';
		}
		out.category = category(bf, sex, out.range, cfg);
		out.band = findBand(sex, years, cfg);

		if (sklad) {
			out.composition = sklad;
			out.lowFfmi = out.composition.ffmi < cfg.ffmi.low[sex];
			// Link do kalkulatora białka tylko przy wyniku w zakresie albo poniżej:
			// przy nadmiarze tłuszczu pierwszy krok to kalorie, nie budowanie masy (D26).
			out.proteinCta = out.lowFfmi && out.category !== 'high' && out.category !== 'obese';
			if (out.range && (out.category === 'high' || out.category === 'obese')) {
				var x = excessFat(out.composition.fat, out.composition.lean, out.range.high);
				out.excess = x > 0 ? x : null;
			}
		}

		return out;
	}

	/**
	 * Stan pusty: przykłady z konfiguracji, wyświetlany %BF.
	 */
	function samples(cfg) {
		return cfg.samples.map(function (s) {
			var raw = rfmBf(s.sex, s, cfg);
			return { sample: s, bf: displayBf(raw) };
		});
	}

	return {
		toNumber: toNumber,
		round1: round1,
		formatDecimal: formatDecimal,
		readField: readField,
		inRange: inRange,
		rfmBf: rfmBf,
		displayBf: displayBf,
		findRange: findRange,
		category: category,
		findBand: findBand,
		composition: composition,
		excessFat: excessFat,
		evaluate: evaluate,
		samples: samples
	};
}));
